// src/admin/AdminBookings.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getFirestore,
  collection,
  query as fsQuery,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  getCountFromServer,
  doc,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * AdminBookings
 * -----------------------------------------------------------------------------
 * Query strategy (minimize reads):
 *  - No onSnapshot listeners; we fetch on explicit user intent (search/apply filters/paginate/refresh).
 *  - One batched page query with composite order + cursor per page.
 *  - One count() aggregation per filter set to show totals (fast & cheap).
 *  - Booking ID search uses a direct getDoc() (single read).
 *  - Debounced/explicit search (Enter key or Search button).
 *
 * Indexes to create (Firestore > Indexes):
 *  - collectionGroup: bookings
 *    Composite: [status ASC, createdAt DESC]
 *    Composite: [mode ASC, createdAt DESC]
 *    Composite: [guestEmailLower ASC, createdAt DESC]
 *    Composite: [hostEmailLower ASC, createdAt DESC]
 *    Composite: [guestPhoneDigits ASC, createdAt DESC]
 *  - Single-field: createdAt (descending) is required.
 *
 * Cloud Functions expected (HTTPS Callable):
 *  - adminRefundBooking   ({ bookingId, amountCents, reason })
 *  - adminCancelBooking   ({ bookingId, policy })             // applies policy + refunds if needed
 *  - adminResendEmails    ({ bookingId, type })               // type: 'receipt'|'itinerary'|'payout'
 *  - adminEditDates       ({ bookingId, startsAt, endsAt })   // validates availability server-side
 *  - adminOverridePrice   ({ bookingId, newTotalCents, note })
 *
 * NOTE: Keep all risky mutations server-side for audit + correctness.
 */

const PAGE_SIZE = 25;
const DEFAULT_RANGE_DAYS = 30;

const STATUS_OPTIONS = [
  { key: "any", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "refunded", label: "Refunded" },
  { key: "canceled", label: "Canceled" },
  { key: "disputed", label: "Disputed" },
];

const MODE_OPTIONS = [
  { key: "any", label: "Any" },
  { key: "day", label: "Day-Use" },
  { key: "overnight", label: "Overnight" },
];

const RESEND_TYPES = [
  { key: "receipt", label: "Receipt" },
  { key: "itinerary", label: "Itinerary" },
  { key: "payout", label: "Payout Notice" },
];

export default function AdminBookings() {
  const db = getFirestore();
  const functions = getFunctions();

  // ---------- Filters / Search ----------
  const [status, setStatus] = useState("any");
  const [mode, setMode] = useState("any");

  const [rangeStart, setRangeStart] = useState(() =>
    toDateInputValue(daysAgo(DEFAULT_RANGE_DAYS))
  );
  const [rangeEnd, setRangeEnd] = useState(() => toDateInputValue(new Date()));

  // Search semantics:
  // - If value looks like a Firestore doc id, we do getDoc.
  // - If looks like email -> search guestEmailLower OR hostEmailLower (prefix).
  // - If digits-only >= 7 -> search guestPhoneDigits (prefix).
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState(""); // snapshot of last applied search

  // ---------- Results / Pagination ----------
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counting, setCounting] = useState(false);
  const [error, setError] = useState(null);

  const [totalCount, setTotalCount] = useState(null);

  // Paged cursors: stack of page cursors so we can go back without requerying previous pages.
  const [page, setPage] = useState(1);
  const cursorsRef = useRef([]); // array of last visible docs for each page
  const lastSnapshotRef = useRef(null); // last page snapshot (to derive next cursor)

  // Detail panel
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [toast, setToast] = useState(null);

  // Memo inputs so we can compare for no-op fetches
  const activeFilters = useMemo(
    () => ({
      status,
      mode,
      rangeStart,
      rangeEnd,
      searchApplied, // note: we fetch only when applied
    }),
    [status, mode, rangeStart, rangeEnd, searchApplied]
  );

  // Apply search on Enter for explicit queries
  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      applySearch();
    }
  };

  const applySearch = useCallback(() => {
    setSearchApplied(search.trim());
    // reset paging
    setPage(1);
    cursorsRef.current = [];
  }, [search]);

  const clearSearch = useCallback(() => {
    setSearch("");
    setSearchApplied("");
    setPage(1);
    cursorsRef.current = [];
  }, []);

  // Derived helpers
  const startTs = useMemo(() => dateInputToTimestamp(rangeStart, true), [rangeStart]);
  const endTs = useMemo(() => dateInputToTimestamp(rangeEnd, false), [rangeEnd]);

  // ---------- Fetch Logic ----------
  const fetchTotalCount = useCallback(async () => {
    setCounting(true);
    try {
      const { q, singleDoc } = buildQuery(db, activeFilters);
      if (singleDoc) {
        // count is either 0 or 1 depending on if doc exists
        const snap = await getDoc(singleDoc);
        setTotalCount(snap.exists() ? 1 : 0);
      } else {
        const c = await getCountFromServer(q);
        setTotalCount(c.data().count || 0);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to count bookings");
      setTotalCount(null);
    } finally {
      setCounting(false);
    }
  }, [db, activeFilters]);

  const fetchPage = useCallback(
    async (direction = "first") => {
      setLoading(true);
      setError(null);
      try {
        const { q, singleDoc } = buildQuery(db, activeFilters, {
          pageSize: PAGE_SIZE,
          cursor: direction === "next" ? lastSnapshotRef.current?.docs?.at(-1) : direction === "prev" ? cursorsRef.current[page - 2] : null,
        });

        if (singleDoc) {
          // Exact booking-id lookup: single read
          const snap = await getDoc(singleDoc);
          lastSnapshotRef.current = null;
          setRows(snap.exists() ? [formatBooking(snap.id, snap.data())] : []);
          return;
        }

        const snapshot = await getDocs(q);
        lastSnapshotRef.current = snapshot;
        const pageRows = snapshot.docs.map((d) => formatBooking(d.id, d.data()));
        setRows(pageRows);

        // Manage cursors for back nav (store the "last visible" of the previous page)
        if (direction === "first") {
          cursorsRef.current = [];
          if (snapshot.docs.length) {
            cursorsRef.current.push(snapshot.docs.at(0)); // first doc of first page
          }
        } else if (direction === "next") {
          if (snapshot.docs.length) {
            cursorsRef.current[page - 1] = snapshot.docs.at(0); // first doc of this page
          }
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to fetch bookings");
      } finally {
        setLoading(false);
      }
    },
    [db, activeFilters, page]
  );

  // Kick initial fetch & when filters change (explicitly)
  useEffect(() => {
    fetchTotalCount();
    fetchPage("first");
  }, [fetchTotalCount, fetchPage]);

  const canNext = useMemo(() => {
    if (totalCount == null) return true;
    const maxPage = Math.ceil(totalCount / PAGE_SIZE) || 1;
    return page < maxPage;
  }, [page, totalCount]);

  const canPrev = page > 1;

  const nextPage = async () => {
    if (!canNext) return;
    setPage((p) => p + 1);
    await fetchPage("next");
  };

  const prevPage = async () => {
    if (!canPrev) return;
    setPage((p) => Math.max(1, p - 1));
    await fetchPage("prev");
  };

  const refreshCurrent = async () => {
    await fetchTotalCount();
    // Re-fetch the current page without moving cursors.
    await fetchPage("first");
    setToast({ type: "success", msg: "Refreshed" });
  };

  // ---------- Actions (mutations via HTTPS Callable) ----------
  const callFn = async (name, payload) => {
    setMutating(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, name);
      const res = await fn(payload);
      setToast({ type: "success", msg: res?.data?.message || "Done" });
      return res?.data;
    } catch (err) {
      console.error(err);
      setError(err.message || `Failed: ${name}`);
      setToast({ type: "error", msg: err.message || "Error" });
      throw err;
    } finally {
      setMutating(false);
    }
  };

  const onRefund = async (bookingId, amountCents, reason) => {
    await callFn("adminRefundBooking", { bookingId, amountCents, reason });
    await refreshCurrent();
  };

  const onCancel = async (bookingId, policy) => {
    await callFn("adminCancelBooking", { bookingId, policy });
    await refreshCurrent();
  };

  const onResend = async (bookingId, type) => {
    await callFn("adminResendEmails", { bookingId, type });
  };

  const onEditDates = async (bookingId, startsAt, endsAt) => {
    await callFn("adminEditDates", {
      bookingId,
      startsAt: Timestamp.fromDate(new Date(startsAt)),
      endsAt: Timestamp.fromDate(new Date(endsAt)),
    });
    await refreshCurrent();
  };

  const onOverridePrice = async (bookingId, newTotalCents, note) => {
    await callFn("adminOverridePrice", { bookingId, newTotalCents, note });
    await refreshCurrent();
  };

  // ---------- CSV export (current page only; no extra reads) ----------
  const exportCsv = () => {
    if (!rows?.length) return;
    const headers = [
      "bookingId",
      "status",
      "mode",
      "createdAt",
      "startsAt",
      "endsAt",
      "guestEmail",
      "hostEmail",
      "guestPhone",
      "total",
      "fees",
      "hostEarnings",
      "payoutStatus",
      "payoutId",
      "paymentIntentId",
    ];
    const lines = [headers.join(",")].concat(
      rows.map((r) =>
        [
          r.id,
          r.status,
          r.mode,
          r.createdAtISO,
          r.startsAtISO,
          r.endsAtISO,
          r.guestEmail || "",
          r.hostEmail || "",
          r.guestPhone || "",
          centsToUsd(r.totalCents),
          centsToUsd(r.feesCents),
          centsToUsd(r.hostEarningsCents),
          r.payoutStatus || "",
          r.payoutId || "",
          r.paymentIntentId || "",
        ]
          .map(csvEscape)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bookings_page_${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- UI ----------
  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl font-semibold mb-4">Bookings & Payouts</h1>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Search</label>
          <div className="flex gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Booking ID, email, or phone"
              className="w-full border rounded-lg px-3 py-2"
            />
            <button
              onClick={applySearch}
              className="px-3 py-2 border rounded-lg hover:bg-gray-50"
              title="Apply search"
            >
              Search
            </button>
            {searchApplied && (
              <button
                onClick={clearSearch}
                className="px-3 py-2 border rounded-lg hover:bg-gray-50"
                title="Clear search"
              >
                Clear
              </button>
            )}
          </div>
          {searchApplied && (
            <p className="text-xs text-gray-500 mt-1">Applied: “{searchApplied}”</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
              cursorsRef.current = [];
            }}
            className="w-full border rounded-lg px-3 py-2"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Mode</label>
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setPage(1);
              cursorsRef.current = [];
            }}
            className="w-full border rounded-lg px-3 py-2"
          >
            {MODE_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Start</label>
          <input
            type="date"
            value={rangeStart}
            onChange={(e) => {
              setRangeStart(e.target.value);
              setPage(1);
              cursorsRef.current = [];
            }}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">End</label>
          <input
            type="date"
            value={rangeEnd}
            onChange={(e) => {
              setRangeEnd(e.target.value);
              setPage(1);
              cursorsRef.current = [];
            }}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        <div className="flex gap-2 md:justify-end md:col-span-6">
          <button
            onClick={refreshCurrent}
            className="px-3 py-2 border rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Refresh
          </button>
          <button
            onClick={exportCsv}
            className="px-3 py-2 border rounded-lg hover:bg-gray-50"
            disabled={!rows?.length}
          >
            Export CSV (page)
          </button>
        </div>
      </div>

      {/* Meta / counts */}
      <div className="flex items-center gap-3 mt-4 text-sm text-gray-600">
        {counting ? (
          <span>Counting…</span>
        ) : totalCount != null ? (
          <span>Total: {totalCount.toLocaleString()}</span>
        ) : (
          <span>—</span>
        )}
        <span className="px-2 py-0.5 rounded bg-gray-100">
          Page {page} · {rows.length} rows
        </span>
        {loading && <span>Loading…</span>}
        {error && <span className="text-red-600">Error: {error}</span>}
        {toast && (
          <span
            className={
              "px-2 py-0.5 rounded " +
              (toast.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700")
            }
          >
            {toast.msg}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <Th>ID</Th>
              <Th>Status</Th>
              <Th>Mode</Th>
              <Th>Created</Th>
              <Th>Starts</Th>
              <Th>Ends</Th>
              <Th>Guest</Th>
              <Th>Host</Th>
              <Th>Total</Th>
              <Th>Host Earn.</Th>
              <Th>Payout</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="text-center py-10 text-gray-500">
                  No bookings found.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                <Td mono title={r.id}>
                  {shorten(r.id)}
                </Td>
                <Td>
                  <Badge kind={statusColor(r.status)}>{r.status}</Badge>
                </Td>
                <Td>{r.mode === "day" ? "Day-Use" : r.mode === "overnight" ? "Overnight" : "—"}</Td>
                <Td mono>{r.createdAtISO || "—"}</Td>
                <Td mono>{r.startsAtISO || "—"}</Td>
                <Td mono>{r.endsAtISO || "—"}</Td>
                <Td>
                  <div className="flex flex-col">
                    <span title={r.guestEmail || ""}>{r.guestEmail || "—"}</span>
                    <span className="text-xs text-gray-500">{r.guestPhone || ""}</span>
                  </div>
                </Td>
                <Td>{r.hostEmail || "—"}</Td>
                <Td mono>{centsToUsd(r.totalCents)}</Td>
                <Td mono>{centsToUsd(r.hostEarningsCents)}</Td>
                <Td>
                  <Badge kind={payoutColor(r.payoutStatus)}>{r.payoutStatus || "—"}</Badge>
                </Td>
                <Td>
                  <button
                    onClick={() => {
                      setSelected(r);
                      setPanelOpen(true);
                    }}
                    className="px-2 py-1 text-xs border rounded hover:bg-gray-100"
                  >
                    Open
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={prevPage}
          disabled={!canPrev || loading}
          className="px-3 py-2 border rounded-lg disabled:opacity-50"
        >
          ← Prev
        </button>
        <button
          onClick={nextPage}
          disabled={!canNext || loading}
          className="px-3 py-2 border rounded-lg disabled:opacity-50"
        >
          Next →
        </button>
      </div>

      {/* Drawer */}
      {panelOpen && selected && (
        <BookingDrawer
          booking={selected}
          onClose={() => setPanelOpen(false)}
          onRefund={onRefund}
          onCancel={onCancel}
          onResend={onResend}
          onEditDates={onEditDates}
          onOverridePrice={onOverridePrice}
          mutating={mutating}
        />
      )}
    </div>
  );
}

/* --------------------------- Drawer / Detail Panel -------------------------- */

function BookingDrawer({
  booking,
  onClose,
  onRefund,
  onCancel,
  onResend,
  onEditDates,
  onOverridePrice,
  mutating,
}) {
  const [refundAmt, setRefundAmt] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [cancelPolicy, setCancelPolicy] = useState("standard");
  const [editStart, setEditStart] = useState(booking.startsAtISO?.slice(0, 16) || "");
  const [editEnd, setEditEnd] = useState(booking.endsAtISO?.slice(0, 16) || "");
  const [overrideTotal, setOverrideTotal] = useState("");

  return (
    <div className="fixed inset-0 bg-black/30 z-40">
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold">Booking {shorten(booking.id)}</h2>
          <button onClick={onClose} className="px-2 py-1 border rounded hover:bg-gray-50">
            Close
          </button>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Facts */}
          <Card title="Facts">
            <Fact label="Status" value={booking.status} badge={statusColor(booking.status)} />
            <Fact label="Mode" value={booking.mode} />
            <Fact label="Created" value={booking.createdAtISO || "—"} mono />
            <Fact label="Starts" value={booking.startsAtISO || "—"} mono />
            <Fact label="Ends" value={booking.endsAtISO || "—"} mono />
            <Fact label="Total" value={centsToUsd(booking.totalCents)} mono />
            <Fact label="Fees" value={centsToUsd(booking.feesCents)} mono />
            <Fact label="Host Earnings" value={centsToUsd(booking.hostEarningsCents)} mono />
          </Card>

          {/* People */}
          <Card title="People">
            <Fact label="Guest Email" value={booking.guestEmail || "—"} />
            <Fact label="Guest Phone" value={booking.guestPhone || "—"} />
            <Fact label="Host Email" value={booking.hostEmail || "—"} />
          </Card>

          {/* Payout */}
          <Card title="Payout">
            <Fact label="Payout Status" value={booking.payoutStatus || "—"} badge={payoutColor(booking.payoutStatus)} />
            <Fact label="Payout ID" value={booking.payoutId || "—"} mono />
            <Fact label="Payment Intent" value={booking.paymentIntentId || "—"} mono />
          </Card>

          {/* Actions */}
          <Card title="Actions">
            <div className="space-y-3">
              <div className="border rounded-lg p-3">
                <div className="font-medium mb-2">Refund</div>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder="Amount (USD)"
                    className="border rounded px-2 py-1 w-32"
                    value={refundAmt}
                    onChange={(e) => setRefundAmt(e.target.value)}
                  />
                  <input
                    placeholder="Reason (optional)"
                    className="border rounded px-2 py-1 flex-1"
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                  />
                  <button
                    disabled={mutating || !refundAmt}
                    onClick={() =>
                      onRefund(booking.id, usdToCents(refundAmt), refundReason || undefined)
                    }
                    className="px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Issue Refund
                  </button>
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="font-medium mb-2">Cancel</div>
                <div className="flex gap-2 items-center">
                  <select
                    className="border rounded px-2 py-1"
                    value={cancelPolicy}
                    onChange={(e) => setCancelPolicy(e.target.value)}
                  >
                    <option value="standard">Standard Policy</option>
                    <option value="flex">Flexible</option>
                    <option value="strict">Strict</option>
                    <option value="force_majeure">Force Majeure</option>
                  </select>
                  <button
                    disabled={mutating}
                    onClick={() => onCancel(booking.id, cancelPolicy)}
                    className="px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel Booking
                  </button>
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="font-medium mb-2">Edit Dates</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="datetime-local"
                    value={editStart}
                    onChange={(e) => setEditStart(e.target.value)}
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="datetime-local"
                    value={editEnd}
                    onChange={(e) => setEditEnd(e.target.value)}
                    className="border rounded px-2 py-1"
                  />
                  <button
                    disabled={mutating || !editStart || !editEnd}
                    onClick={() => onEditDates(booking.id, editStart, editEnd)}
                    className="px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Save Dates
                  </button>
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="font-medium mb-2">Resend Email</div>
                <div className="flex gap-2 items-center">
                  {RESEND_TYPES.map((t) => (
                    <button
                      key={t.key}
                      disabled={mutating}
                      onClick={() => onResend(booking.id, t.key)}
                      className="px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="font-medium mb-2">Override Total</div>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder="New total (USD)"
                    className="border rounded px-2 py-1 w-40"
                    value={overrideTotal}
                    onChange={(e) => setOverrideTotal(e.target.value)}
                  />
                  <button
                    disabled={mutating || !overrideTotal}
                    onClick={() => onOverridePrice(booking.id, usdToCents(overrideTotal), "Admin override")}
                    className="px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Save Override
                  </button>
                </div>
              </div>
            </div>
          </Card>

          {/* Timeline / Notes placeholder (read-only if present) */}
          <Card title="Timeline">
            {Array.isArray(booking.timeline) && booking.timeline.length > 0 ? (
              <ul className="space-y-2">
                {booking.timeline.map((ev, i) => (
                  <li key={i} className="text-sm">
                    <span className="text-gray-500 mr-2">{ev?.at || ""}</span>
                    <span>{ev?.msg || ""}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No events recorded.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Helpers --------------------------------- */

function buildQuery(db, filters, opts = {}) {
  const { pageSize = PAGE_SIZE, cursor = null } = opts;
  const { status, mode, rangeStart, rangeEnd, searchApplied } = filters;

  const bookingsCol = collection(db, "bookings");

  // 1) Booking ID exact lookup — single document read, zero index needs.
  if (looksLikeDocId(searchApplied)) {
    return { q: null, singleDoc: doc(db, "bookings", searchApplied) };
  }

  // 2) Determine search dimension (email or phone), otherwise fallback to status/mode/date
  const constraints = [];

  // Date range (always include, index on createdAt DESC)
  const startTs = dateInputToTimestamp(rangeStart, true);
  const endTs = dateInputToTimestamp(rangeEnd, false);
  constraints.push(where("createdAt", ">=", startTs));
  constraints.push(where("createdAt", "<=", endTs));
  constraints.push(orderBy("createdAt", "desc"));

  // Mode/status equality filters if specified
  if (mode !== "any") {
    constraints.unshift(where("mode", "==", mode));
  }
  if (status !== "any") {
    constraints.unshift(where("status", "==", status));
  }

  // Smart search branching (prefix queries require precomputed "search fields")
  const term = (searchApplied || "").trim().toLowerCase();

  if (looksLikeEmail(term)) {
    // prefix search on guestEmailLower or hostEmailLower (two queries merged client-side would double reads).
    // To keep reads down: prefer guestEmailLower; if you want OR, mirror email into a unified array "emails[]" and run array-contains (exact).
    constraints.unshift(where("guestEmailLower", ">=", term));
    constraints.unshift(where("guestEmailLower", "<=", term + "\uf8ff"));
  } else if (looksLikePhoneDigits(term)) {
    constraints.unshift(where("guestPhoneDigits", ">=", term));
    constraints.unshift(where("guestPhoneDigits", "<=", term + "\uf8ff"));
  } else if (term) {
    // If arbitrary text, avoid wasteful queries. Require explicit fields.
    // You can add additional normalized fields and branch here as needed.
  }

  const q = fsQuery(bookingsCol, ...constraints, limit(pageSize), ...(cursor ? [startAfter(cursor)] : []));
  return { q, singleDoc: null };
}

function formatBooking(id, d = {}) {
  const createdAt = tsToDate(d.createdAt);
  const startsAt = tsToDate(d.startsAt);
  const endsAt = tsToDate(d.endsAt);
  return {
    id,
    status: d.status || "—",
    mode: d.mode || "—",
    createdAtISO: createdAt ? toIsoLocal(createdAt) : null,
    startsAtISO: startsAt ? toIsoLocal(startsAt) : null,
    endsAtISO: endsAt ? toIsoLocal(endsAt) : null,
    guestEmail: d.guestEmail || d.guestEmailLower || null,
    hostEmail: d.hostEmail || d.hostEmailLower || null,
    guestPhone: d.guestPhone || null,
    totalCents: d.totalCents ?? 0,
    feesCents: d.feesCents ?? 0,
    hostEarningsCents: d.hostEarningsCents ?? 0,
    payoutStatus: d.payoutStatus || null,
    payoutId: d.payoutId || null,
    paymentIntentId: d.paymentIntentId || null,
    timeline: d.timeline || [],
  };
}

function toIsoLocal(date) {
  // ISO without trailing Z (local)
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function tsToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Timestamp) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  if (typeof ts === "string" || typeof ts === "number") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

function toDateInputValue(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateInputToTimestamp(v, startOfDay) {
  // Converts YYYY-MM-DD to Timestamp
  if (!v) return Timestamp.fromDate(new Date(0));
  const [y, m, d] = v.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(y, (m || 1) - 1, d || 1, startOfDay ? 0 : 23, startOfDay ? 0 : 59, startOfDay ? 0 : 59);
  return Timestamp.fromDate(dt);
}

function looksLikeDocId(s) {
  return !!s && /^[A-Za-z0-9_-]{16,}$/.test(s);
}
function looksLikeEmail(s) {
  return !!s && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}
function looksLikePhoneDigits(s) {
  return !!s && /^[0-9]{7,15}$/.test(s.replace(/\D/g, ""));
}

function usdToCents(u) {
  const n = Number(u);
  if (isNaN(n)) return 0;
  return Math.round(n * 100);
}
function centsToUsd(c) {
  const n = (c || 0) / 100;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function shorten(id, n = 6) {
  if (!id) return "";
  if (id.length <= n * 2 + 1) return id;
  return `${id.slice(0, n)}…${id.slice(-n)}`;
}
function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/* --------------------------------- UI Bits --------------------------------- */

function Th({ children }) {
  return <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide">{children}</th>;
}
function Td({ children, mono, title }) {
  return (
    <td className={`px-3 py-2 align-top ${mono ? "font-mono text-xs" : ""}`} title={title}>
      {children}
    </td>
  );
}
function Badge({ children, kind = "gray" }) {
  const styles = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    slate: "bg-slate-100 text-slate-700",
  }[kind];
  return <span className={`px-2 py-0.5 rounded text-xs ${styles}`}>{children}</span>;
}
function statusColor(status) {
  switch (status) {
    case "paid":
      return "green";
    case "refunded":
      return "yellow";
    case "canceled":
      return "red";
    case "disputed":
      return "blue";
    default:
      return "gray";
  }
}
function payoutColor(s) {
  switch (s) {
    case "pending":
      return "yellow";
    case "paid":
      return "green";
    case "failed":
      return "red";
    default:
      return "slate";
  }
}
function Card({ title, children }) {
  return (
    <div className="border rounded-lg">
      <div className="p-3 border-b font-medium">{title}</div>
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
}
function Fact({ label, value, mono, badge }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-gray-500 text-sm">{label}</div>
      <div className={"text-sm " + (mono ? "font-mono" : "")}>
        {badge ? <Badge kind={badge}>{value}</Badge> : value}
      </div>
    </div>
  );
}
